/**
 * Live validation (plan E02-T03): the spec is checked by the API as it is edited, a moment after the
 * last change, the way it will be checked on save: the schema, the providers, and, once those pass,
 * the tenant's warnings (credentials, compliance). The last result stays shown while the next runs.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { data, type Schemas } from "../../api/client";
import { useBff, useMe } from "../../app/session";
import { SpecDocument, type Path } from "./specDocument";
import type { Builder } from "./useBuilder";

export type Severity = "error" | "warning" | "info";

export interface Issue {
  path: string;
  segments: Path;
  code: string;
  message: string;
  severity: Severity;
}

export type VariableUse = Schemas["VariableUse"];

export interface Validation {
  issues: Issue[];
  /** Every {{scope.name}} the agent uses, where (V1.1-EA-T05), from the last check that parsed. */
  variables: VariableUse[];
  /** Checking the latest edit. */
  checking: boolean;
  /** The issues at exactly this path. */
  at: (path: Path) => Issue[];
}

const DELAY_MS = 400;

/** "spec.tools.1.http.url" -> ["spec", "tools", 1, "http", "url"] */
export function segments(path: string): Path {
  return path.split(".").map((part) => (/^\d+$/.test(part) ? Number(part) : part));
}

export function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export function useValidation(builder: Builder): Validation {
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const text = useSettled(builder.problems.length ? null : builder.text, DELAY_MS);
  const result = useQuery({
    queryKey: ["agents:validate", tenant, text],
    queryFn: () => {
      const parsed = SpecDocument.parse(text ?? ""); // the text that settled, not the one being typed
      const body = (parsed.ok ? parsed.doc.toJSON() : {}) as Schemas["AgentDocument"];
      return data(api.POST("/v1/agents:validate", { body }));
    },
    enabled: text !== null,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
    retry: false,
  });
  const issues: Issue[] = useMemo(
    () => [
      ...(result.data?.errors ?? []).map((e) => ({ ...e, segments: segments(e.path), severity: "error" as const })),
      ...(result.data?.warnings ?? []).map((w) => ({ ...w, segments: segments(w.path), severity: w.severity })),
    ],
    [result.data],
  );
  return {
    issues,
    variables: result.data?.variables ?? [],
    checking: result.isFetching || text !== (builder.problems.length ? null : builder.text),
    at: (path) => issues.filter((i) => i.path === path.join(".")),
  };
}
